import { ROI_CALCULATORS, NO_PAYBACK, type RoiCalculatorId, type RoiResult } from './engine.js';
import { ROI_LABELS } from './labels.js';

/**
 * What the simulator shows for a result: the same texts, built the same way
 * (numbers in the en-US format in both calculators, as the simulator did),
 * keyed by the simulator's element IDs. Two deliberate changes (brief
 * section 10): a value that cannot be computed (a division by zero) shows a
 * dash instead of NaN or ∞, and a payback without profit shows "No payback"
 * instead of 999.00. "Year 10 ROI" is now roiOverYears (engine.ts).
 */

/** Shown for a value that cannot be computed (zero sorters, zero robots...). */
export const ROI_NOT_COMPUTABLE = '—';

/** The simulator's fmt(): rounded, thousands separated. */
export function roiFmt(num: number): string {
  return Math.round(num).toLocaleString('en-US');
}

/** The simulator's fmt2(): two decimals, thousands separated. */
export function roiFmt2(num: number): string {
  return (Math.round(num * 100) / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const shown = (num: number, text: (n: number) => string) =>
  Number.isFinite(num) ? text(num) : ROI_NOT_COMPUTABLE;

export const ROI_DISPLAY_KEYS = [
  'robotKgDisplay',
  'robotKgHint',
  'tonsPerSorterDay',
  'totalSorterTonsDisplay',
  'disposalCostTotal',
  'sorterRevenue',
  'sorterMargin',
  'sorterSalariesTotal',
  'tonsPerRobotDay',
  'totalRobotTons',
  'robotDisposalAvoid',
  'annualOpCost',
  'payback',
  'roiYear1',
  'roiYear5',
  'roiOverYears',
  'robotProfit',
  'robotMargin',
  'compRobotTons',
  'compHumanTons',
  'compRobotRev',
  'compHumanRev',
  'compRobotProfCol',
  'compHumanProfCol',
  'compRobotMarginCol',
  'compHumanMarginCol',
  'compRobotRatioCol',
  'compHumanRatioCol',
  's1Sorters',
  's1RobotsDisplay',
  's1SortersTri',
  's1RobotsTri',
  's1TotalTri',
  's1VsStatusPct',
  's1Salaries',
  's1RobotsCost',
  's1TotalCost',
  's1Revenue',
  's1Profit',
  's1ProfitVsPct',
  's2Sorters',
  's2RobotsDisplay',
  's2SortersTri',
  's2RobotsTri',
  's2TotalTri',
  's2VsStatusPct',
  's2Salaries',
  's2RobotsCost',
  's2TotalCost',
  's2Revenue',
  's2Profit',
  's2ProfitVsPct',
  's2SalarySavings',
  's10SortersDisplay',
  's10RobotsDisplay',
  's10ExtraHoursDisplay',
  's10SortersExtra',
  's10RobotsExtra',
  's10TotalExtra',
  's10VsStatusPct',
  's10Salaries',
  's10RobotsCost',
  's10TotalCost',
  's10Revenue',
  's10Profit',
  's10ProfitVsPct',
  'hoursInfo',
] as const;

export type RoiDisplayKey = (typeof ROI_DISPLAY_KEYS)[number];

/** Section 6 rows: the better column is shown in green, the other in red. */
export type RoiComparisonRow = 'tons' | 'revenue' | 'profit' | 'margin' | 'ratio';

export interface RoiDisplay {
  text: Record<RoiDisplayKey, string>;
  winners: Record<RoiComparisonRow, 'robot' | 'human'>;
  /** The label of roiOverYears, N being the depreciation period. */
  roiOverYearsLabel: string;
}

export function roiDisplay(result: RoiResult, calculator: RoiCalculatorId): RoiDisplay {
  const { symbol, lang } = ROI_CALCULATORS[calculator];
  const labels = ROI_LABELS[lang];
  const v = result.values;

  const int = (n: number) => shown(n, roiFmt);
  const dec = (n: number) => shown(n, roiFmt2);
  const money = (n: number) => shown(n, (x) => symbol + roiFmt(x));
  const pct = (n: number) => shown(n, (x) => roiFmt2(x) + '%');
  const signedPct = (n: number) => shown(n, (x) => (x >= 0 ? '+' : '') + roiFmt2(x) + '%');
  // The simulator wrote some counts as they were, without formatting.
  const raw = (n: number) => shown(n, String);

  const h = result.humanKgPerPick;
  const kgHint = Number.isFinite(h)
    ? `${labels.kgHint.baseline}: ${roiFmt2(h)}kg | ${labels.kgHint.optimized}: ${(h * 1.5).toFixed(2)}-${(h * 3).toFixed(2)}kg`
    : '';

  const robots = result.robotsFor100k;
  const humans = result.humansFor100k;
  const add = result.addRobots;
  const replace = result.replaceSorters;
  const ext = result.extendedHours;

  const text: Record<RoiDisplayKey, string> = {
    robotKgDisplay: shown(v.robotKgPerPick, (x) => x.toFixed(2) + ' kg'),
    robotKgHint: kgHint,
    tonsPerSorterDay: dec(result.tonsPerSorterDay),
    totalSorterTonsDisplay: int(result.totalSorterTons),
    disposalCostTotal: money(result.disposalCostTotal),
    sorterRevenue: money(result.sorterRevenue),
    sorterMargin: pct(result.sorterMargin),
    sorterSalariesTotal: money(result.sorterSalariesTotal),
    tonsPerRobotDay: dec(result.tonsPerRobotDay),
    totalRobotTons: int(result.totalRobotTons),
    robotDisposalAvoid: money(result.robotDisposalAvoid),
    annualOpCost: money(result.annualOpCost),
    payback: result.payback === NO_PAYBACK ? labels.noPayback : dec(result.payback),
    roiYear1: pct(result.roiYear1),
    roiYear5: pct(result.roiYear5),
    roiOverYears: pct(result.roiOverYears),
    robotProfit: money(result.robotAnnualProfit),
    robotMargin: pct(result.robotMargin),
    compRobotTons: int(robots.tons),
    compHumanTons: int(humans.tons),
    compRobotRev: money(robots.revenue),
    compHumanRev: money(humans.revenue),
    compRobotProfCol: money(robots.profit),
    compHumanProfCol: money(humans.profit),
    compRobotMarginCol: pct(robots.margin),
    compHumanMarginCol: pct(humans.margin),
    compRobotRatioCol: dec(robots.ratio),
    compHumanRatioCol: dec(humans.ratio),
    s1Sorters: raw(add.sorters),
    s1RobotsDisplay: raw(add.robots),
    s1SortersTri: int(add.sortersTons),
    s1RobotsTri: int(add.robotsTons),
    s1TotalTri: int(add.totalTons),
    s1VsStatusPct: signedPct(add.vsStatusPct),
    s1Salaries: money(add.salaries),
    s1RobotsCost: money(add.robotsCost),
    s1TotalCost: money(add.totalCost),
    s1Revenue: money(add.revenue),
    s1Profit: money(add.profit),
    s1ProfitVsPct: signedPct(add.profitVsPct),
    s2Sorters: raw(replace.sorters),
    s2RobotsDisplay: raw(replace.robots),
    s2SortersTri: int(replace.sortersTons),
    s2RobotsTri: int(replace.robotsTons),
    s2TotalTri: int(replace.totalTons),
    s2VsStatusPct: signedPct(replace.vsStatusPct),
    s2Salaries: money(replace.salaries),
    s2RobotsCost: money(replace.robotsCost),
    s2TotalCost: money(replace.totalCost),
    s2Revenue: money(replace.revenue),
    s2Profit: money(replace.profit),
    s2ProfitVsPct: signedPct(replace.profitVsPct),
    s2SalarySavings: money(replace.salarySavings),
    s10SortersDisplay: raw(ext.sorters),
    s10RobotsDisplay: raw(ext.robots),
    s10ExtraHoursDisplay: int(ext.extraHoursPerYear),
    s10SortersExtra: int(ext.sortersExtra),
    s10RobotsExtra: int(ext.robotsExtra),
    s10TotalExtra: int(ext.totalExtra),
    s10VsStatusPct: signedPct(ext.vsStatusPct),
    s10Salaries: money(ext.salaries),
    s10RobotsCost: money(ext.robotsCost),
    s10TotalCost: money(ext.totalCost),
    s10Revenue: money(ext.revenue),
    s10Profit: money(ext.profit),
    s10ProfitVsPct: signedPct(ext.profitVsPct),
    hoursInfo: labels.hoursInfo[v.hoursMode],
  };

  // The simulator: the robot column wins only when strictly greater.
  const win = (robot: number, human: number) => (robot > human ? 'robot' : 'human');
  return {
    text,
    winners: {
      tons: win(robots.tons, humans.tons),
      revenue: win(robots.revenue, humans.revenue),
      profit: win(robots.profit, humans.profit),
      margin: win(robots.margin, humans.margin),
      ratio: win(robots.ratio, humans.ratio),
    },
    roiOverYearsLabel: labels.roiOverYears(String(v.depreciationPeriod)),
  };
}
